/**
 * Conteo V8 — Análisis ExG (modelo conteo_7.py)
 *
 * Auto-carga el perímetro del lote y los bloques guardados.
 * Heatmap ExG TURBO blended 45/55 — análisis de caminos y cobertura.
 * La auto-detección corre en Web Worker para no bloquear el browser.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, FlaskConical, Upload, ZoomIn, ZoomOut, RotateCcw, Loader2, AlertCircle, Trash2, Layers, Download, CheckCircle2, Sprout, X } from 'lucide-react';
import { supabase } from '../../supabase';
import { invocarMotorPythonBloquesPoligonoArchivo, type MotorPythonBatchResponse } from './deteccionService';

// ─── TURBO colormap LUT (256 × RGB) ─────────────────────────────────────────
const TURBO_R = new Uint8Array([48,50,52,53,55,57,59,60,62,64,66,68,70,72,74,76,78,80,82,84,86,88,90,92,94,96,98,100,102,104,106,108,110,112,114,116,118,119,121,123,124,126,128,129,131,132,134,135,137,138,139,141,142,143,144,145,146,147,148,149,150,151,151,152,153,153,154,154,155,155,155,156,156,156,156,156,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,157,158,159,161,163,165,167,169,172,174,176,178,181,183,185,188,190,193,195,198,200,203,205,208,210,213,215,218,220,223,225,228,230,233,235,237,239,241,243,245,247,249,250,252,253,254,255,255,255,255,255,255,255,255,255,255,254,253,252,251,249,248,246,244,242,240,238,235,233,231,228,226,223,220,217,215,212,209,206,203,200,197,194,191,188,185,182,179,175,172,169,166,163,160,157,153,150,147,144,141,138,135,131,128]);
const TURBO_G = new Uint8Array([18,20,22,25,27,29,32,34,36,39,41,44,46,49,51,54,57,59,62,64,67,70,72,75,78,80,83,85,88,91,93,96,98,101,103,106,108,111,113,116,118,120,123,125,127,130,132,134,136,138,141,143,145,147,149,151,153,155,157,158,160,162,163,165,167,168,170,171,173,174,175,177,178,179,180,181,183,184,185,185,186,187,188,188,189,189,190,190,190,191,191,191,191,191,191,191,190,190,190,189,189,188,188,187,186,185,184,183,182,181,180,179,177,176,175,173,172,170,168,167,165,163,161,159,157,155,153,151,149,147,145,143,140,138,136,133,131,128,126,123,121,118,116,113,111,108,105,103,100,97,95,92,89,87,84,81,78,76,73,70,67,65,62,59,56,53,51,48,45,42,39,37,34,31,28,26,23,20,18,15,13,10,8,6,4,2,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,2,4,5,7,9,11,13,15,17,19,21,24,26,28,31,33,36,38,41,44,46,49,52,55,57,60,63,66,69,72,75]);
const TURBO_B = new Uint8Array([117,120,124,128,132,136,140,144,147,151,155,159,162,166,170,173,177,180,184,187,190,193,197,200,203,206,209,212,214,217,219,222,224,226,228,230,232,234,236,237,238,240,241,242,243,244,245,245,246,246,247,247,247,247,247,247,247,246,246,245,245,244,243,243,242,241,240,239,238,237,236,234,233,232,230,229,227,226,224,222,221,219,217,215,213,211,209,207,205,203,201,198,196,194,191,189,186,184,181,178,176,173,170,167,165,162,159,156,153,150,147,144,141,138,135,132,129,126,123,120,116,113,110,107,104,101,98,95,91,88,85,82,79,76,73,70,67,64,61,58,55,52,49,46,43,40,37,35,32,29,27,24,22,19,17,14,12,10,8,7,5,4,2,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]);

function turbo(i8: number): [number, number, number] {
  const i = Math.max(0, Math.min(255, i8));
  return [TURBO_R[i], TURBO_G[i], TURBO_B[i]];
}

// ─── Types ───────────────────────────────────────────────────────────────────
type Pt = [number, number]; // normalizado 0..1 relativo a la imagen

export interface ConteoPlantasV8Props {
  fincaId?: string | number;
  rasterPngUrl?: string;
  rasterBounds?: [[number, number], [number, number]]; // [[minLat,minLng],[maxLat,maxLng]]
  nombreLote?: string;
  geojsonLote?: any;
  empresaId?: number;
  onClose?: () => void;
  gsdM?: number;
  bloquesDB?: Array<{ num: number; area_ha?: number; plant_count?: number; conteo_resultado_payload?: any }>;
}

// ─── Conversión GeoJSON WGS84 → coordenadas normalizadas de imagen ────────────
// bounds = [[minLat,minLng],[maxLat,maxLng]]  (igual que Leaflet)
function wgs84ToNorm(lat: number, lng: number, bounds: [[number,number],[number,number]]): Pt {
  const [[minLat, minLng], [maxLat, maxLng]] = bounds;
  const x = (lng - minLng) / Math.max(1e-10, maxLng - minLng);
  // La imagen tiene minLat arriba (lat invertida respecto a Y de imagen)
  const y = 1 - (lat - minLat) / Math.max(1e-10, maxLat - minLat);
  return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))];
}

function geojsonToNorm(gj: any, bounds: [[number,number],[number,number]]): Pt[] {
  let geom = gj?.type === 'Feature' ? gj.geometry : gj;
  if (!geom) return [];
  let ring: number[][] = [];
  if (geom.type === 'Polygon') ring = geom.coordinates[0];
  else if (geom.type === 'MultiPolygon') ring = geom.coordinates[0][0];
  else return [];
  const pts = ring.map(([lng, lat]) => wgs84ToNorm(lat, lng, bounds));
  // cerrar el anillo → quitar último si es igual al primero
  if (pts.length > 1 &&
      Math.abs(pts[0][0] - pts[pts.length-1][0]) < 1e-6 &&
      Math.abs(pts[0][1] - pts[pts.length-1][1]) < 1e-6) {
    pts.pop();
  }
  return pts;
}

// ─── Helpers geométricos ─────────────────────────────────────────────────────
function clamp01(v: number) { return Math.max(0, Math.min(1, v)); }

function areaM2(pts: Pt[], imgW: number, imgH: number, gsd: number): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
  }
  return Math.abs(a) / 2 * imgW * imgH * gsd * gsd;
}

function distM(a: Pt, b: Pt, w: number, h: number, gsd: number) {
  const dx = (b[0]-a[0])*w, dy = (b[1]-a[1])*h;
  return Math.sqrt(dx*dx+dy*dy)*gsd;
}

function svgPts(pts: Pt[]) { return pts.map(p=>`${(p[0]*100).toFixed(3)},${(p[1]*100).toFixed(3)}`).join(' '); }
function centroid(pts: Pt[]): Pt {
  if (!pts.length) return [0,0];
  return [pts.reduce((s,p)=>s+p[0],0)/pts.length, pts.reduce((s,p)=>s+p[1],0)/pts.length];
}
function fmtM2(v: number) { return v>=10000?`${(v/10000).toFixed(2)} ha`:`${Math.round(v).toLocaleString('es-CR')} m²`; }
function fmtPct(v: number) { return v.toLocaleString('es-CR',{minimumFractionDigits:1,maximumFractionDigits:1})+'%'; }

// ─── Rasterizar polígono → máscara binaria ───────────────────────────────────
function polyMask(pts: Pt[], w: number, h: number): Uint8Array {
  const c = document.createElement('canvas'); c.width=w; c.height=h;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle='#fff'; ctx.beginPath();
  ctx.moveTo(pts[0][0]*w, pts[0][1]*h);
  for(let i=1;i<pts.length;i++) ctx.lineTo(pts[i][0]*w, pts[i][1]*h);
  ctx.closePath(); ctx.fill();
  const d=ctx.getImageData(0,0,w,h).data;
  const m=new Uint8Array(w*h);
  for(let i=0;i<w*h;i++) m[i]=d[i*4]>0?1:0;
  return m;
}
function orM(a: Uint8Array, b: Uint8Array) { const o=new Uint8Array(a.length); for(let i=0;i<a.length;i++) o[i]=a[i]|b[i]; return o; }
function andM(a: Uint8Array, b: Uint8Array) { const o=new Uint8Array(a.length); for(let i=0;i<a.length;i++) o[i]=a[i]&b[i]; return o; }
function count(m: Uint8Array) { let s=0; for(let i=0;i<m.length;i++) s+=m[i]; return s; }

// ─── Heatmap ExG + blend (hilo principal — imagen escalada a ≤1400px) ────────
function buildHeat(img: HTMLImageElement): { canvas: HTMLCanvasElement; exg: Float32Array; exgMin: number; exgMax: number; w: number; h: number } {
  const MAX=1400, sc=Math.min(1,MAX/Math.max(img.naturalWidth,img.naturalHeight));
  const w=Math.round(img.naturalWidth*sc), h=Math.round(img.naturalHeight*sc);
  const src=document.createElement('canvas'); src.width=w; src.height=h;
  src.getContext('2d')!.drawImage(img,0,0,w,h);
  const px=src.getContext('2d')!.getImageData(0,0,w,h).data;
  const exg=new Float32Array(w*h);
  let mn=Infinity, mx=-Infinity;
  for(let i=0;i<w*h;i++){
    const R=px[i*4],G=px[i*4+1],B=px[i*4+2];
    const v=2*G-R-B; exg[i]=v;
    if(v<mn)mn=v; if(v>mx)mx=v;
  }
  const rng=Math.max(1e-6,mx-mn);
  const out=document.createElement('canvas'); out.width=w; out.height=h;
  const ctx=out.getContext('2d')!; const id=ctx.createImageData(w,h); const d=id.data;
  for(let i=0;i<w*h;i++){
    const n=(exg[i]-mn)/rng;
    const [tr,tg,tb]=turbo(Math.round(n*255));
    d[i*4]  =Math.round(px[i*4]  *0.45+tr*0.55);
    d[i*4+1]=Math.round(px[i*4+1]*0.45+tg*0.55);
    d[i*4+2]=Math.round(px[i*4+2]*0.45+tb*0.55);
    d[i*4+3]=px[i*4+3];
  }
  ctx.putImageData(id,0,0);
  return {canvas:out, exg, exgMin:mn, exgMax:mx, w, h};
}

// ─── Leyenda de colores ExG ───────────────────────────────────────────────────
// Muestra la barra TURBO con etiquetas: Tierra/Camino → Vegetación baja → Siembra alta
function LeyendaExG() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(()=>{
    const c=canvasRef.current; if(!c) return;
    const ctx=c.getContext('2d')!; const w=c.width, h=c.height;
    for(let x=0;x<w;x++){
      const [r,g,b]=turbo(Math.round((x/w)*255));
      ctx.fillStyle=`rgb(${r},${g},${b})`; ctx.fillRect(x,0,1,h);
    }
  },[]);
  return (
    <div className="px-3 pb-3 shrink-0" style={{borderBottom:'1px solid var(--line-dim)'}}>
      <p className="text-[10px] mb-1 font-semibold uppercase tracking-wide" style={{color:'var(--ink-faint)'}}>Índice ExG (vegetación)</p>
      <canvas ref={canvasRef} width={220} height={14} className="w-full rounded" style={{display:'block'}}/>
      <div className="flex justify-between mt-0.5 text-[9px]" style={{color:'var(--ink-faint)'}}>
        <span>◀ Tierra/Camino</span>
        <span>Vegetación baja</span>
        <span>Siembra alta ▶</span>
      </div>
    </div>
  );
}

// ─── Worker inline para auto-detección de caminos ────────────────────────────
const WORKER_SRC = `
function erode(m,w,h,k){const r=Math.floor(k/2);const t=new Uint8Array(m.length);for(let y=0;y<h;y++){for(let x=0;x<w;x++){let ok=1;for(let dy=-r;dy<=r&&ok;dy++){for(let dx=-r;dx<=r&&ok;dx++){const ny=y+dy,nx=x+dx;if(ny<0||ny>=h||nx<0||nx>=w||!m[ny*w+nx])ok=0;}}t[y*w+x]=ok;}}m.set(t);}
function dilate(m,w,h,k){const r=Math.floor(k/2);const t=new Uint8Array(m.length);for(let y=0;y<h;y++){for(let x=0;x<w;x++){let ok=0;for(let dy=-r;dy<=r&&!ok;dy++){for(let dx=-r;dx<=r&&!ok;dx++){const ny=y+dy,nx=x+dx;if(ny>=0&&ny<h&&nx>=0&&nx<w&&m[ny*w+nx])ok=1;}}t[y*w+x]=ok;}}m.set(t);}
function cc(m,w,h,minA,minW,minL){const lb=new Int32Array(m.length);let nxt=1;const stk=[];for(let i=0;i<m.length;i++){if(m[i]&&!lb[i]){lb[i]=nxt;stk.push(i);while(stk.length){const idx=stk.pop();const y=Math.floor(idx/w),x=idx%w;for(const[dy,dx]of[[-1,0],[1,0],[0,-1],[0,1]]){const ny=y+dy,nx=x+dx;if(ny>=0&&ny<h&&nx>=0&&nx<w){const ni=ny*w+nx;if(m[ni]&&!lb[ni]){lb[ni]=nxt;stk.push(ni);}}}}nxt++;}}const out=new Uint8Array(m.length);for(let lab=1;lab<nxt;lab++){let cnt=0,x0=w,x1=0,y0=h,y1=0;for(let i=0;i<m.length;i++){if(lb[i]===lab){cnt++;const y=Math.floor(i/w),x=i%w;if(x<x0)x0=x;if(x>x1)x1=x;if(y<y0)y0=y;if(y>y1)y1=y;}}const ww=x1-x0+1,hh=y1-y0+1,c=Math.min(ww,hh),l=Math.max(ww,hh);if(cnt>=minA&&c>=minW&&l>=minL){for(let i=0;i<m.length;i++)if(lb[i]===lab)out[i]=1;}}return out;}
self.onmessage=function(e){
  const{exg8,maskArea,maskSiembra,w,h,aMinPx,wMinPx,lMinPx,umbPct}=e.data;
  // Sólo analizamos la zona del lote que NO es bloque de siembra
  const maskAnalisis=new Uint8Array(w*h);
  for(let i=0;i<w*h;i++) maskAnalisis[i]=maskArea[i]&(maskSiembra[i]^1);
  const vals=[];
  for(let i=0;i<w*h;i++) if(maskAnalisis[i]) vals.push(exg8[i]);
  vals.sort((a,b)=>a-b);
  const umb=vals.length?vals[Math.floor(vals.length*(umbPct/100))]:128;
  const mVeg=new Uint8Array(w*h);
  for(let i=0;i<w*h;i++) if(maskAnalisis[i]&&exg8[i]>=umb) mVeg[i]=1;
  erode(mVeg,w,h,3); dilate(mVeg,w,h,7);
  const mCam=new Uint8Array(w*h);
  for(let i=0;i<w*h;i++) mCam[i]=maskAnalisis[i]&(mVeg[i]^1);
  erode(mCam,w,h,9); dilate(mCam,w,h,17);
  const limpio=cc(mCam,w,h,aMinPx,wMinPx,lMinPx);
  self.postMessage({limpio},[limpio.buffer]);
};
`;

const DETECT_MAX = 700;

async function runDetectWorker(
  exgData: Float32Array, exgMin: number, exgMax: number,
  maskArea: Uint8Array, maskSiembra: Uint8Array,
  wFull: number, hFull: number,
  anchoM: number, largoM: number, gsd: number, umbPct: number
): Promise<Uint8Array> {
  const sc = Math.min(1, DETECT_MAX/Math.max(wFull,hFull));
  const w = Math.round(wFull*sc), h = Math.round(hFull*sc);
  const rng = Math.max(1e-6, exgMax-exgMin);
  const exg8 = new Uint8Array(w*h);
  const mA = new Uint8Array(w*h);
  const mS = new Uint8Array(w*h);
  for(let y=0;y<h;y++) for(let x=0;x<w;x++){
    const sx=Math.min(Math.round(x/sc),wFull-1), sy=Math.min(Math.round(y/sc),hFull-1);
    const si=sy*wFull+sx;
    exg8[y*w+x]=Math.max(0,Math.min(255,Math.round(((exgData[si]-exgMin)/rng)*255)));
    mA[y*w+x]=maskArea[si];
    mS[y*w+x]=maskSiembra[si];
  }
  const aMinPx=Math.max(8,Math.round(8/(gsd/sc)**2));
  const wMinPx=Math.max(2,Math.round(anchoM/(gsd/sc)));
  const lMinPx=Math.max(4,Math.round(largoM/(gsd/sc)));

  return new Promise(res=>{
    const blob=new Blob([WORKER_SRC],{type:'application/javascript'});
    const url=URL.createObjectURL(blob);
    const wk=new Worker(url);
    wk.postMessage({exg8,maskArea:mA,maskSiembra:mS,w,h,aMinPx,wMinPx,lMinPx,umbPct},[exg8.buffer,mA.buffer,mS.buffer]);
    wk.onmessage=ev=>{
      URL.revokeObjectURL(url); wk.terminate();
      const sm: Uint8Array=ev.data.limpio;
      const full=new Uint8Array(wFull*hFull);
      for(let y=0;y<hFull;y++) for(let x=0;x<wFull;x++){
        full[y*wFull+x]=sm[Math.min(Math.round(y*sc),h-1)*w+Math.min(Math.round(x*sc),w-1)];
      }
      res(full);
    };
  });
}

// ─── Construir overlay de clasificación ExG con 4 colores ────────────────────
// azul=camino/tierra | amarillo=veg.baja | verde=veg.media | verde vivo=siembra
function buildClassCanvas(
  exgData: Float32Array, _exgMin: number, _exgMax: number,
  maskLote: Uint8Array, maskBloques: Uint8Array, maskCaminos: Uint8Array,
  w: number, h: number, alpha=140
): HTMLCanvasElement {
  const c=document.createElement('canvas'); c.width=w; c.height=h;
  const ctx=c.getContext('2d')!; const id=ctx.createImageData(w,h); const d=id.data;
  // Cuartiles del ExG dentro del lote para clasificar
  const vals: number[]=[];
  for(let i=0;i<w*h;i++) if(maskLote[i]) vals.push(exgData[i]);
  vals.sort((a,b)=>a-b);
  const q1=vals[Math.floor(vals.length*0.25)]??0;
  const q2=vals[Math.floor(vals.length*0.50)]??0;
  const q3=vals[Math.floor(vals.length*0.75)]??0;
  for(let i=0;i<w*h;i++){
    if(!maskLote[i]) continue;
    const v=exgData[i];
    let r=0,g=0,b=0;
    if(maskCaminos[i]){
      // Camino detectado → naranja
      r=255;g=120;b=0;
    } else if(maskBloques[i]){
      // Bloque de siembra → verde vivo
      r=0;g=220;b=60;
    } else {
      // Zona entre bloques: clasificar por cuartil ExG
      if(v<=q1){r=60;g=80;b=220;}         // azul — tierra/camino
      else if(v<=q2){r=200;g=190;b=20;}   // amarillo — veg baja
      else if(v<=q3){r=60;g=200;b=80;}    // verde medio
      else{r=0;g=255;b=60;}               // verde vivo — veg alta
    }
    d[i*4]=r; d[i*4+1]=g; d[i*4+2]=b; d[i*4+3]=alpha;
  }
  ctx.putImageData(id,0,0);
  return c;
}

// ─── Componente principal ─────────────────────────────────────────────────────
function V8Content({fincaId, empresaId, rasterPngUrl: rasterProp, rasterBounds, nombreLote, gsdM, geojsonLote, onClose, bloquesDB}: ConteoPlantasV8Props) {
  const gsd = Math.max(0.001, Number(gsdM)||0.05);

  // Imagen
  const [imgSrc, setImgSrc] = useState<string>(rasterProp||'');
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgErr, setImgErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Heatmap / clasificación
  const [heatUrl, setHeatUrl]   = useState('');
  const [classUrl, setClassUrl] = useState('');
  const [buildingHeat, setBuildingHeat] = useState(false);
  const [showMode, setShowMode] = useState<'heat'|'class'|'rgb'>('heat');
  const exgRef = useRef<{exg:Float32Array;min:number;max:number;w:number;h:number}|null>(null);
  const natRef = useRef<{w:number;h:number}>({w:1,h:1});

  // Zoom/pan
  const [zoom, setZoom] = useState(1);
  const [pan, setPan]   = useState(false);
  const panR  = useRef<{x:number;y:number;sl:number;st:number}|null>(null);
  const moved = useRef(false);
  const conR  = useRef<HTMLDivElement>(null);
  const imgR  = useRef<HTMLImageElement>(null);

  // Modo dibujo
  const [modo, setModo] = useState<'camino'|'regla'>('camino');
  const [puntos, setPts] = useState<Pt[]>([]);

  // Caminos manuales
  const [caminos, setCam] = useState<{pts:Pt[];areaM2:number}[]>([]);

  // Auto-detección
  const [autoMask, setAutoMask] = useState<Uint8Array|null>(null);
  const [autoUrl, setAutoUrl]   = useState('');
  const [autoRun, setAutoRun]   = useState(false);
  const [anchoMin, setAncho]    = useState(2.2);
  const [largoMin, setLargo]    = useState(8.0);
  const [umbral, setUmbral]     = useState(58);

  // Regla
  const [reglaPts, setRegla] = useState<Pt[]>([]);

  // Capas visibles
  const [showLote, setShowLote]    = useState(true);
  const [showBlqs, setShowBlqs]    = useState(true);
  const [showAuto, setShowAuto]    = useState(true);
  const [showClass, setShowClass2] = useState(false);

  // Alerta
  const [alerta, setAlerta] = useState('');

  // Bloque seleccionado (resaltado en imagen)
  const [bloqueSelec, setBloqueSelec] = useState<number | null>(null);
  const [bloqueHover, setBloqueHover] = useState<number | null>(null);

  // Ficha PDF
  const [fichaOpen, setFichaOpen] = useState(false);

  // Motor conteo
  const [tifMotorFile, setTifMotorFile] = useState<File | null>(null);
  const [gsdMotor, setGsdMotor] = useState('0.05');
  const [camasOverride, setCamasOverride] = useState('');
  const [filasCama, setFilasCama] = useState('4');
  const [motorRunning, setMotorRunning] = useState(false);
  const [motorErr, setMotorErr] = useState('');
  const [motorResult, setMotorResult] = useState<MotorPythonBatchResponse | null>(null);
  const [motorSaved, setMotorSaved] = useState(false);
  const tifMotorRef = useRef<HTMLInputElement>(null);

  // ── Bloques: combina los recibidos por prop + auto-carga desde DB ──
  const [bloquesRaw, setBloquesRaw] = useState<Array<{num:number;geojson?:any;plant_count?:number;area_ha?:number;conteo_resultado_payload?:any}>>(bloquesDB||[]);

  useEffect(()=>{
    // Si ya vienen por prop con datos, no hace falta cargar
    const tienenPuntos = (bloquesDB||[]).some(b=>b.conteo_resultado_payload?.puntos?.length>=3);
    if(tienenPuntos){ setBloquesRaw(bloquesDB!); return; }
    // Si no, cargar directamente desde Supabase (incluye plant_count y area_ha)
    if(!fincaId) return;
    supabase.from('emp_bloques')
      .select('num,geojson,plant_count,area_ha,conteo_resultado_payload')
      .eq('lote_id', fincaId)
      .order('num')
      .then(({data})=>{ if(data?.length) setBloquesRaw(data); });
  // eslint-disable-next-line
  },[fincaId]);

  // ── Perímetro del lote en coords normalizadas ──
  const perimLote: Pt[] = useMemo(()=>{
    if(!geojsonLote||!rasterBounds) return [];
    try { return geojsonToNorm(geojsonLote, rasterBounds); } catch { return []; }
  },[geojsonLote, rasterBounds]);

  // ── Bloques en coords normalizadas ──
  // Prioridad: puntos normalizados del payload → geojson WGS84 del bloque → descartar
  const bloquesNorm = useMemo(()=>{
    return bloquesRaw.flatMap(b=>{
      const meta = { plant_count: b.plant_count, area_ha: b.area_ha };
      // 1. puntos normalizados guardados por el motor
      if(Array.isArray(b.conteo_resultado_payload?.puntos)&&b.conteo_resultado_payload.puntos.length>=3){
        return [{num:b.num, pts:b.conteo_resultado_payload.puntos as Pt[], ...meta}];
      }
      // 2. geojson WGS84 del bloque (dibujado sobre el mapa Leaflet)
      if(b.geojson&&rasterBounds){
        try{
          const pts=geojsonToNorm(b.geojson, rasterBounds);
          if(pts.length>=3) return [{num:b.num, pts, ...meta}];
        }catch{/* ignorar */}
      }
      return [];
    });
  },[bloquesRaw, rasterBounds]);

  // ── Máscara combinada de bloques ──
  const getMaskBloques = useCallback((w:number,h:number): Uint8Array =>{
    let m=new Uint8Array(w*h);
    for(const b of bloquesNorm) m=orM(m,polyMask(b.pts,w,h));
    return m;
  },[bloquesNorm]);

  // ── Procesar imagen: carga en un Image() object, no depende del onLoad del DOM ──
  const procesarImagen = useCallback((src: string)=>{
    if(!src) return;
    setBuildingHeat(true);
    setHeatUrl(''); setClassUrl('');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = ()=>{
      natRef.current={w:img.naturalWidth,h:img.naturalHeight};
      // Dar un tick para que React actualice hasImage
      setTimeout(()=>{
        try{
          const res=buildHeat(img);
          exgRef.current={exg:res.exg,min:res.exgMin,max:res.exgMax,w:res.w,h:res.h};
          res.canvas.toBlob(b=>{ if(b) setHeatUrl(URL.createObjectURL(b)); },'image/png');
          // Clasificación con los bloques que haya en ese momento
          const {w,h}=res;
          const mLote=perimLote.length>=3?polyMask(perimLote,w,h):new Uint8Array(w*h).fill(1);
          const mBlq=getMaskBloques(w,h);
          const mCam=new Uint8Array(w*h);
          const cc=buildClassCanvas(res.exg,res.exgMin,res.exgMax,mLote,mBlq,mCam,w,h);
          cc.toBlob(b=>{if(b)setClassUrl(URL.createObjectURL(b));setBuildingHeat(false);},'image/png');
        }catch{setBuildingHeat(false);}
      },50);
    };
    img.onerror=()=>{ setBuildingHeat(false); setImgErr('No se pudo cargar la imagen.'); };
    img.src=src;
  // eslint-disable-next-line
  },[perimLote, getMaskBloques]);

  // ── Disparar procesamiento cuando cambia la imagen fuente ──
  useEffect(()=>{ procesarImagen(imgSrc); },[imgSrc]); // eslint-disable-line

  // ── Reconstruir clasificación cuando cambian capas (autoMask, caminos, bloques) ──
  useEffect(()=>{
    const d=exgRef.current; if(!d) return;
    const {exg,min,max,w,h}=d;
    const mLote=perimLote.length>=3?polyMask(perimLote,w,h):new Uint8Array(w*h).fill(1);
    const mBlq=getMaskBloques(w,h);
    let mCam=new Uint8Array(w*h);
    for(const c of caminos) mCam=orM(mCam,polyMask(c.pts,w,h));
    if(autoMask) mCam=orM(mCam,andM(autoMask,mLote));
    const cc=buildClassCanvas(exg,min,max,mLote,mBlq,mCam,w,h);
    cc.toBlob(b=>{if(b)setClassUrl(URL.createObjectURL(b));},'image/png');
  // eslint-disable-next-line
  },[autoMask, caminos, bloquesNorm, perimLote, getMaskBloques]);

  // ── Carga manual TIF/PNG ──
  async function cargarImagen(file:File){
    setLoadingImg(true);setImgErr('');
    try{
      if(/\.(tif|tiff)$/i.test(file.name)){
        const {fromBlob}=await import('geotiff');
        const tiff=await fromBlob(file); const image=await tiff.getImage();
        const w=image.getWidth(),h=image.getHeight();
        const rasters=await image.readRasters({interleave:true}) as unknown as ArrayLike<number>;
        const spp=image.getSamplesPerPixel();
        let lo=Infinity,hi=-Infinity;
        for(let i=0;i<rasters.length;i++){const v=Number(rasters[i]);if(v<lo)lo=v;if(v>hi)hi=v;}
        if(!isFinite(lo)||lo===hi){lo=0;hi=255;}
        const rng=Math.max(1e-6,hi-lo); const to8=(v:number)=>Math.max(0,Math.min(255,Math.round(((v-lo)/rng)*255)));
        const cv=document.createElement('canvas');cv.width=w;cv.height=h;
        const ctx=cv.getContext('2d')!;const id=ctx.createImageData(w,h);
        for(let i=0;i<w*h;i++){
          const dst=i*4;
          if(spp>=3){id.data[dst]=to8(Number(rasters[i*spp]));id.data[dst+1]=to8(Number(rasters[i*spp+1]));id.data[dst+2]=to8(Number(rasters[i*spp+2]));}
          else{const v=to8(Number(rasters[i]));id.data[dst]=id.data[dst+1]=id.data[dst+2]=v;}
          id.data[dst+3]=255;
        }
        ctx.putImageData(id,0,0);
        cv.toBlob(b=>{if(b)setImgSrc(URL.createObjectURL(b));},'image/png');
      }else{setImgSrc(URL.createObjectURL(file));}
    }catch(e:any){setImgErr(e?.message||'Error');}
    setLoadingImg(false);
  }

  // ── Teclado ──
  useEffect(()=>{
    function ok(e:KeyboardEvent){
      const tag=(e.target as HTMLElement)?.tagName?.toLowerCase();
      if(tag==='input'||tag==='textarea'||tag==='select') return;
      if(e.key==='Backspace'||e.key==='Delete'){e.preventDefault();setPts(p=>p.slice(0,-1));}
      else if((e.ctrlKey||e.metaKey)&&e.key.toLowerCase()==='z'){e.preventDefault();setPts(p=>p.slice(0,-1));}
      else if(e.key==='Escape') setPts([]);
    }
    window.addEventListener('keydown',ok);
    return ()=>window.removeEventListener('keydown',ok);
  },[]);

  // ── Scroll al bloque seleccionado ──
  useEffect(()=>{
    if(bloqueSelec===null||!conR.current||!imgR.current) return;
    const blk=bloquesNorm.find(b=>b.num===bloqueSelec);
    if(!blk||!blk.pts.length) return;
    const c=centroid(blk.pts); // [0..1, 0..1]
    const container=conR.current;
    const imgEl=imgR.current;
    // Posición en px del centroide dentro del canvas escalado
    const scaledW=imgEl.offsetWidth*zoom;
    const scaledH=imgEl.offsetHeight*zoom;
    const cx=c[0]*scaledW;
    const cy=c[1]*scaledH;
    // Centrar en el contenedor
    container.scrollLeft=cx-container.clientWidth/2;
    container.scrollTop=cy-container.clientHeight/2;
  // eslint-disable-next-line
  },[bloqueSelec, bloquesNorm, zoom]);

  // ── Click imagen ──
  const onImgClick=useCallback((e:React.MouseEvent<HTMLImageElement>)=>{
    if(moved.current||!imgR.current) return;
    const r=imgR.current.getBoundingClientRect();
    const pt:Pt=[clamp01((e.clientX-r.left)/r.width),clamp01((e.clientY-r.top)/r.height)];
    if(modo==='regla'){setRegla(p=>[...p,pt]);return;}
    setPts(p=>[...p,pt]);
  },[modo]);

  // ── Pan ──
  function startPan(e:React.MouseEvent<HTMLDivElement>){
    if(zoom<=1) return; e.preventDefault(); moved.current=false;
    panR.current={x:e.clientX,y:e.clientY,sl:conR.current?.scrollLeft??0,st:conR.current?.scrollTop??0};
    setPan(true);
  }
  function movePan(e:React.MouseEvent<HTMLDivElement>){
    if(!pan||!panR.current||!conR.current) return; e.preventDefault();
    const dx=e.clientX-panR.current.x,dy=e.clientY-panR.current.y;
    if(Math.abs(dx)>2||Math.abs(dy)>2) moved.current=true;
    conR.current.scrollLeft=panR.current.sl-dx; conR.current.scrollTop=panR.current.st-dy;
  }
  function endPan(){panR.current=null;setPan(false);setTimeout(()=>{moved.current=false;},0);}
  const onWheel=useCallback((e:React.WheelEvent)=>{
    e.preventDefault(); setZoom(z=>e.deltaY<0?Math.min(8,+(z+0.25).toFixed(2)):Math.max(0.25,+(z-0.25).toFixed(2)));
  },[]);

  // ── Guardar camino manual ──
  function guardarCamino(){
    if(puntos.length<3){setAlerta('Mínimo 3 puntos.');return;}
    const {w,h}=natRef.current;
    setCam(c=>[...c,{pts:[...puntos],areaM2:areaM2(puntos,w,h,gsd)}]);
    setPts([]);setAlerta('');
  }

  // ── Auto-detectar caminos ──
  async function autoDetectar(){
    if(!exgRef.current){setAlerta('Esperá a que cargue el heatmap.');return;}
    if(!perimLote.length&&!bloquesNorm.length){setAlerta('El lote no tiene perímetro ni bloques para acotar el análisis.');return;}
    setAutoRun(true);setAlerta('');
    try{
      const {exg,min,max,w,h}=exgRef.current;
      const mLote=perimLote.length>=3?polyMask(perimLote,w,h):new Uint8Array(w*h).fill(1);
      const mBlq=getMaskBloques(w,h);
      const mask=await runDetectWorker(exg,min,max,mLote,mBlq,w,h,anchoMin,largoMin,gsd,umbral);
      // Intersectar con lote para no salir del perímetro
      const maskFinal=andM(mask,mLote);
      setAutoMask(maskFinal);
      // Overlay naranja
      const oc=document.createElement('canvas');oc.width=w;oc.height=h;
      const octx=oc.getContext('2d')!;const oid=octx.createImageData(w,h);
      for(let i=0;i<w*h;i++) if(maskFinal[i]){oid.data[i*4]=255;oid.data[i*4+1]=100;oid.data[i*4+2]=0;oid.data[i*4+3]=180;}
      octx.putImageData(oid,0,0);
      oc.toBlob(b=>{if(b)setAutoUrl(URL.createObjectURL(b));},'image/png');
      // Activar vista clasificación automáticamente
      setShowMode('class');setShowClass2(true);
    }catch(e:any){setAlerta(e?.message||'Error en auto-detección');}
    setAutoRun(false);
  }

  // ── Conteo con motor Python ──
  async function contarPlantas() {
    if (!tifMotorFile) { setMotorErr('Cargá el archivo TIF del ortomosaico primero.'); return; }
    if (bloquesNorm.length === 0) { setMotorErr('El lote no tiene bloques dibujados. Agregá bloques en Catálogo → Lotes.'); return; }
    setMotorRunning(true); setMotorErr(''); setMotorResult(null); setMotorSaved(false);
    try {
      const gsdVal = Math.max(0.01, Number(gsdMotor) || 0.05);
      const camasVal = camasOverride.trim() ? Math.max(1, Math.round(Number(camasOverride))) : undefined;
      const filasVal = Math.max(1, Math.round(Number(filasCama) || 4));
      const res = await invocarMotorPythonBloquesPoligonoArchivo(
        { minArea: 20, maxArea: 800 },
        bloquesNorm.map(b => ({ bloque_num: b.num, puntos: b.pts })),
        tifMotorFile,
        null,
        String(fincaId || 'upload'),
        { areaMode: 'fixed_gsd', gsdM: gsdVal, detectMode: 'conteo5_camas', expectedBeds: camasVal, filasPorCama: filasVal }
      );
      setMotorResult(res);
    } catch (e: any) {
      setMotorErr(e?.message || String(e));
    } finally {
      setMotorRunning(false);
    }
  }

  async function guardarConteo() {
    if (!motorResult?.bloques?.length || !fincaId || !empresaId) return;
    try {
      for (const b of motorResult.bloques) {
        if (b.bloque_num == null || !b.conteo) continue;
        await supabase.from('emp_bloques').upsert({
          empresa_id: empresaId,
          lote_id: fincaId,
          num: b.bloque_num,
          tipo: 'siembra',
          area_ha: b.area_ha,
          plant_count: b.conteo,
          conteo_detectado_at: new Date().toISOString(),
          conteo_modo: 'rgb',
          conteo_resultado_payload: b,
          activo: true,
        }, { onConflict: 'empresa_id,lote_id,num' });
      }
      setMotorSaved(true);
    } catch (e: any) {
      setMotorErr(e?.message ?? 'Error al guardar.');
    }
  }

  // ── Totales ──
  const totales = useMemo(()=>{
    if(!exgRef.current) return null;
    const {w,h}=exgRef.current;
    const mLote=perimLote.length>=3?polyMask(perimLote,w,h):null;
    const mBlq=bloquesNorm.length?getMaskBloques(w,h):null;
    let mCam=new Uint8Array(w*h);
    for(const c of caminos) mCam=orM(mCam,polyMask(c.pts,w,h));
    if(autoMask&&mLote) mCam=orM(mCam,andM(autoMask,mLote));
    const areaTotalM2=mLote?count(mLote)*gsd*gsd:0;
    const areaCaminosM2=mLote?count(andM(mCam,mLote))*gsd*gsd:count(mCam)*gsd*gsd;
    const areaSiembraM2=mBlq?count(mLote?andM(mBlq,mLote):mBlq)*gsd*gsd:0;
    const areaEntreBloquesM2=Math.max(0,areaTotalM2-areaSiembraM2);
    const areaCaminosEntreBloques=Math.min(areaCaminosM2,areaEntreBloquesM2);
    const coberturaPct=areaTotalM2>0?(areaSiembraM2/areaTotalM2)*100:0;
    const caminosPct=areaTotalM2>0?(areaCaminosM2/areaTotalM2)*100:0;
    return {areaTotalM2,areaCaminosM2,areaSiembraM2,areaEntreBloquesM2,areaCaminosEntreBloques,coberturaPct,caminosPct};
    // eslint-disable-next-line
  },[perimLote,bloquesNorm,caminos,autoMask,gsd]);

  const hasImage=!!imgSrc&&!loadingImg;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{background:'var(--surface-base)'}}>

      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-4 py-2 shrink-0" style={{borderBottom:'1px solid var(--line)',background:'var(--surface-raised)'}}>
        <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs shrink-0"
          style={{borderColor:'var(--line)',color:'var(--ink-muted)'}}>
          <ArrowLeft size={13}/> Volver
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-sm truncate" style={{color:'var(--ink)'}}>
            <FlaskConical size={13} className="inline mr-1" style={{color:'var(--emp-accent-txt)'}}/>
            Análisis ExG{nombreLote?` · ${nombreLote}`:''}
          </h1>
          <p className="text-[10px]" style={{color:'var(--ink-faint)'}}>
            ExG TURBO 45/55 · GSD={gsd} m/px
            {perimLote.length>0&&' · perímetro cargado'}
            {bloquesNorm.length>0&&` · ${bloquesNorm.length} bloque${bloquesNorm.length>1?'s':''}`}
          </p>
        </div>
        {/* Vista */}
        {hasImage&&(<>
          {(['heat','class','rgb'] as const).map(m=>(
            <button key={m} onClick={()=>setShowMode(m)}
              className="px-2 py-1 rounded text-[11px] border shrink-0"
              style={{borderColor:showMode===m?'var(--emp-accent)':'var(--line)',color:showMode===m?'var(--emp-accent-txt)':'var(--ink-faint)',background:showMode===m?'var(--emp-accent-bg)':'transparent'}}>
              {m==='heat'?'Heatmap':m==='class'?'Clasificación':'RGB'}
            </button>
          ))}
        </>)}
        {bloquesNorm.length > 0 && (
          <button onClick={()=>setFichaOpen(true)}
            className="inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs shrink-0 font-semibold"
            style={{borderColor:'#60a5fa55',color:'#60a5fa',background:'#60a5fa11'}}>
            <Download size={12}/> Ficha PDF
          </button>
        )}
        <button onClick={()=>fileRef.current?.click()} className="inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs shrink-0"
          style={{borderColor:'var(--line)',color:'var(--ink-muted)'}}>
          <Upload size={12}/>{!rasterProp&&' Cargar imagen'}
        </button>
        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.tif,.tiff" className="hidden"
          onChange={e=>{const f=e.target.files?.[0];if(f)cargarImagen(f);e.target.value='';}}/>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Imagen */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">

          {/* Toolbar modo */}
          {hasImage&&(
            <div className="flex items-center gap-2 px-3 py-1.5 shrink-0 flex-wrap"
              style={{borderBottom:'1px solid var(--line)',background:'var(--surface-raised)'}}>
              {/* Modos */}
              {(['camino','regla'] as const).map(m=>(
                <button key={m} onClick={()=>{ if(m==='regla'){ setModo(v=>{ if(v==='regla'){setRegla([]);return 'camino';} return 'regla'; }); } else setModo(m); }}
                  className="px-2.5 py-1.5 rounded text-xs font-semibold border"
                  style={{borderColor:modo===m?(m==='camino'?'#ff7800':'#60a5fa'):'var(--line)',background:modo===m?(m==='camino'?'#ff780022':'#60a5fa22'):'transparent',color:modo===m?(m==='camino'?'#ff7800':'#60a5fa'):'var(--ink-faint)'}}>
                  {m==='camino'?'⬛ Marcar Camino':'📏 Regla'}
                </button>
              ))}
              {modo==='camino'&&puntos.length>=3&&(
                <button onClick={guardarCamino} className="px-2.5 py-1.5 rounded text-xs font-semibold"
                  style={{background:'#ff780033',color:'#ff7800',border:'1px solid #ff780066'}}>
                  + Guardar Camino
                </button>
              )}
              {puntos.length>0&&<button onClick={()=>setPts([])} className="text-xs px-2 py-1.5 rounded border"
                style={{borderColor:'var(--line)',color:'var(--ink-faint)'}}>Cancelar</button>}
              {modo==='regla'&&reglaPts.length>0&&<button onClick={()=>setRegla([])} className="text-xs px-2 py-1.5 rounded border"
                style={{borderColor:'var(--line)',color:'var(--ink-faint)'}}>Limpiar regla</button>}

              <div className="h-4 border-l mx-1" style={{borderColor:'var(--line)'}}/>

              {/* Capas */}
              {perimLote.length>0&&(
                <button onClick={()=>setShowLote(v=>!v)} className="px-2 py-1 rounded text-[11px] border"
                  style={{borderColor:showLote?'#fff':'var(--line)',color:showLote?'#fff':'var(--ink-faint)'}}>
                  <Layers size={11} className="inline mr-0.5"/> Perímetro
                </button>
              )}
              {bloquesNorm.length>0&&(
                <button onClick={()=>setShowBlqs(v=>!v)} className="px-2 py-1 rounded text-[11px] border"
                  style={{borderColor:showBlqs?'#22c55e':'var(--line)',color:showBlqs?'#22c55e':'var(--ink-faint)'}}>
                  <Layers size={11} className="inline mr-0.5"/> Bloques
                </button>
              )}
              {autoUrl&&(
                <button onClick={()=>setShowAuto(v=>!v)} className="px-2 py-1 rounded text-[11px] border"
                  style={{borderColor:showAuto?'#ff7800':'var(--line)',color:showAuto?'#ff7800':'var(--ink-faint)'}}>
                  <Layers size={11} className="inline mr-0.5"/> Caminos AI
                </button>
              )}
              {classUrl&&(
                <button onClick={()=>setShowClass2(v=>!v)} className="px-2 py-1 rounded text-[11px] border"
                  style={{borderColor:showClass?'#a78bfa':'var(--line)',color:showClass?'#a78bfa':'var(--ink-faint)'}}>
                  <Layers size={11} className="inline mr-0.5"/> Clasificación
                </button>
              )}

              <div className="h-4 border-l mx-1" style={{borderColor:'var(--line)'}}/>

              <button onClick={()=>setZoom(z=>Math.min(8,+(z+0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70">
                <ZoomIn size={14} style={{color:'var(--ink-muted)'}}/>
              </button>
              <span className="text-xs font-mono w-10 text-center" style={{color:'var(--ink-faint)'}}>{(zoom*100).toFixed(0)}%</span>
              <button onClick={()=>setZoom(z=>Math.max(0.25,+(z-0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70">
                <ZoomOut size={14} style={{color:'var(--ink-muted)'}}/>
              </button>
              <button onClick={()=>{setZoom(1);if(conR.current){conR.current.scrollLeft=0;conR.current.scrollTop=0;}}} className="p-1 rounded hover:opacity-70">
                <RotateCcw size={13} style={{color:'var(--ink-muted)'}}/>
              </button>
              {buildingHeat&&<span className="text-[10px] flex items-center gap-1 ml-1" style={{color:'var(--ink-faint)'}}><Loader2 size={11} className="animate-spin"/>calculando…</span>}
            </div>
          )}

          {/* Sin imagen */}
          {!hasImage&&!loadingImg&&(
            <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{opacity:0.7}}>
              <Upload size={48} style={{color:'var(--ink-faint)'}}/>
              <p className="text-sm" style={{color:'var(--ink-faint)'}}>{rasterProp?'Cargando imagen del lote…':'Cargá una imagen para comenzar'}</p>
              {imgErr&&<p className="text-xs text-red-400">{imgErr}</p>}
            </div>
          )}
          {loadingImg&&(
            <div className="flex-1 flex items-center justify-center gap-2">
              <Loader2 size={28} className="animate-spin" style={{color:'var(--emp-accent)'}}/>
              <span className="text-sm" style={{color:'var(--ink-faint)'}}>Procesando imagen...</span>
            </div>
          )}

          {/* Canvas zoom */}
          <div ref={conR} className="flex-1 min-h-0 overflow-auto" style={{display:hasImage?'block':'none',background:'#06080f'}}
            onMouseMove={movePan} onMouseUp={endPan} onMouseLeave={endPan} onWheel={onWheel}>
            <div className="flex items-start justify-start min-h-full">
              <div className="relative inline-block origin-top-left"
                style={{transform:`scale(${zoom})`,transformOrigin:'top left',cursor:pan?'grabbing':zoom>1?'grab':'crosshair'}}
                onMouseDown={startPan}>

                {/* Imagen activa — siempre una sola visible, el ref apunta a la activa */}
                {/* La imagen RGB siempre está en el DOM (invisible si no es la vista activa)
                    para que el ref exista y las dimensiones sean correctas para el SVG */}
                <img ref={imgR} src={imgSrc} alt="ortofoto"
                  className="block max-w-none select-none"
                  style={{maxWidth:'none', opacity: showMode==='rgb'?1:0, position: showMode==='rgb'?'relative':'absolute', top:0, left:0, pointerEvents: showMode==='rgb'?'auto':'none'}}
                  draggable={false} onClick={onImgClick}/>
                {heatUrl&&<img src={heatUrl} alt="heat"
                  className="block max-w-none select-none"
                  style={{maxWidth:'none', position: showMode==='heat'?'relative':'absolute', top:0, left:0, opacity: showMode==='heat'?1:0, pointerEvents: showMode==='heat'?'auto':'none'}}
                  draggable={false} onClick={onImgClick}/>}
                {classUrl&&<img src={classUrl} alt="class"
                  className="block max-w-none select-none"
                  style={{maxWidth:'none', position: showMode==='class'?'relative':'absolute', top:0, left:0, opacity: showMode==='class'?1:0, pointerEvents: showMode==='class'?'auto':'none'}}
                  draggable={false} onClick={onImgClick}/>}

                {/* Overlay caminos auto (sobre cualquier vista) */}
                {autoUrl&&showAuto&&showMode!=='class'&&(
                  <img src={autoUrl} alt="auto"
                    className="absolute inset-0 max-w-none pointer-events-none"
                    style={{maxWidth:'none',width:'100%',height:'100%'}}/>
                )}

                {/* SVG capas vectoriales */}
                <svg className="absolute inset-0 w-full h-full" style={{pointerEvents:'none'}} viewBox="0 0 100 100" preserveAspectRatio="none">

                  {/* Perímetro lote (blanco punteado) */}
                  {showLote&&perimLote.length>2&&(
                    <polygon points={svgPts(perimLote)} fill="rgba(255,255,255,0.04)" stroke="#fff" strokeWidth="0.3" strokeDasharray="1,0.5"/>
                  )}

                  {/* Bloques del DB (verde sólido; amarillo cuando está seleccionado/hover) */}
                  {showBlqs&&bloquesNorm.map(b=>{
                    const c=centroid(b.pts);
                    const sel=bloqueSelec===b.num;
                    const hov=bloqueHover===b.num;
                    const active=sel||hov;
                    return <g key={`b${b.num}`}
                      style={{pointerEvents:'all',cursor:'pointer'}}
                      onClick={()=>setBloqueSelec(v=>v===b.num?null:b.num)}
                      onMouseEnter={()=>setBloqueHover(b.num)}
                      onMouseLeave={()=>setBloqueHover(null)}>
                      <polygon points={svgPts(b.pts)} fill={active?"rgba(250,204,21,0.22)":"rgba(34,197,94,0.2)"} stroke={active?"#facc15":"#22c55e"} strokeWidth={active?0.35:0.25}/>
                      <text x={c[0]*100} y={c[1]*100} fontSize={active?"2.5":"2"} fill={active?"#facc15":"#22c55e"} textAnchor="middle" dominantBaseline="middle" fontWeight="bold"
                        style={{filter:'drop-shadow(0 0 2px #000)',pointerEvents:'none'}}>B{b.num}</text>
                    </g>;
                  })}


                  {/* Caminos manuales (naranja) */}
                  {caminos.map((c,i)=>{
                    const cc=centroid(c.pts);
                    return <g key={`C${i}`}>
                      <polygon points={svgPts(c.pts)} fill="rgba(255,120,0,0.22)" stroke="#ff7800" strokeWidth="0.2"/>
                      <text x={cc[0]*100} y={cc[1]*100} fontSize="1.8" fill="#ff7800" textAnchor="middle" dominantBaseline="middle" fontWeight="bold">C{i+1}</text>
                    </g>;
                  })}

                  {/* En construcción */}
                  {puntos.length>=2&&<polyline points={svgPts(puntos)} fill="none" stroke="#ff7800" strokeWidth="0.2" strokeDasharray="1,0.5"/>}
                  {puntos.length>=3&&<line x1={puntos[puntos.length-1][0]*100} y1={puntos[puntos.length-1][1]*100} x2={puntos[0][0]*100} y2={puntos[0][1]*100} stroke="#ff7800" strokeWidth="0.15" strokeDasharray="1,0.5"/>}
                  {puntos.map((p,i)=><circle key={i} cx={p[0]*100} cy={p[1]*100} r="0.4" fill="#ff7800"/>)}

                  {/* Regla */}
                  {reglaPts.map((p,i)=><circle key={`rp${i}`} cx={p[0]*100} cy={p[1]*100} r="0.4" fill="#60a5fa"/>)}
                  {reglaPts.length>=2&&reglaPts.map((p,i)=>{
                    if(i===0) return null;
                    const prev=reglaPts[i-1];
                    const mx=(p[0]+prev[0])/2*100, my=(p[1]+prev[1])/2*100;
                    const d=distM(prev,p,natRef.current.w,natRef.current.h,gsd);
                    return <g key={`rl${i}`}>
                      <line x1={prev[0]*100} y1={prev[1]*100} x2={p[0]*100} y2={p[1]*100} stroke="#60a5fa" strokeWidth="0.22"/>
                      <text x={mx} y={my-0.9} fontSize="1.8" fill="#60a5fa" textAnchor="middle" fontWeight="bold"
                        style={{filter:'drop-shadow(0 0 1.5px #000)'}}>{d.toFixed(1)} m</text>
                    </g>;
                  })}
                </svg>

                {/* Popup flotante del bloque seleccionado / hover */}
                {showBlqs&&(bloqueHover??bloqueSelec)!=null&&(()=>{
                  const activeNum=bloqueHover??bloqueSelec;
                  const b=bloquesNorm.find(b=>b.num===activeNum);
                  if(!b||!b.pts.length) return null;
                  const c=centroid(b.pts); // [0..1, 0..1]
                  const plM2=b.plant_count&&b.area_ha&&b.area_ha>0
                    ? (b.plant_count/(b.area_ha*10000)).toFixed(2) : null;
                  const plHa=b.plant_count&&b.area_ha&&b.area_ha>0
                    ? Math.round(b.plant_count/b.area_ha).toLocaleString('es-CR') : null;
                  return (
                    <div style={{
                      position:'absolute',
                      left:`calc(${c[0]*100}% + 12px)`,
                      top:`calc(${c[1]*100}% - 90px)`,
                      zIndex:10,
                      pointerEvents: bloqueSelec===activeNum && bloqueHover==null ? 'all' : 'none',
                      background:'rgba(12,18,28,0.93)',
                      border:'1px solid rgba(250,204,21,0.55)',
                      borderRadius:8,
                      padding:'8px 12px',
                      fontSize:11,
                      color:'#e5e7eb',
                      lineHeight:1.7,
                      backdropFilter:'blur(6px)',
                      boxShadow:'0 6px 24px rgba(0,0,0,0.7)',
                      whiteSpace:'nowrap',
                      minWidth:140,
                    }}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                        <span style={{fontWeight:700,color:'#facc15',fontSize:12}}>Bloque: {activeNum}</span>
                        {bloqueSelec===activeNum&&bloqueHover==null&&(
                          <button onClick={()=>setBloqueSelec(null)}
                            style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',padding:'0 0 0 8px',lineHeight:1,fontSize:13}}>✕</button>
                        )}
                      </div>
                      <div style={{borderTop:'1px solid rgba(255,255,255,0.1)',marginBottom:5}}/>
                      <div><span style={{color:'#9ca3af'}}>Plantas: </span><span style={{fontWeight:600,color:'#fff'}}>{b.plant_count!=null?b.plant_count.toLocaleString('es-CR'):'—'}</span></div>
                      <div><span style={{color:'#9ca3af'}}>Área: </span><span style={{fontWeight:600}}>{b.area_ha!=null?b.area_ha.toFixed(2)+' ha':'—'}</span></div>
                      <div><span style={{color:'#9ca3af'}}>Densidad: </span><span style={{fontWeight:600}}>{plHa!=null?plHa+' pl/ha':'—'}</span></div>
                      <div><span style={{color:'#9ca3af'}}>pl/m²: </span><span style={{fontWeight:600,color:'#34d399'}}>{plM2??'—'}</span></div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* ── Panel derecho ── */}
        <div className="w-72 shrink-0 flex flex-col min-h-0 overflow-y-auto"
          style={{borderLeft:'1px solid var(--line)',background:'var(--surface-raised)'}}>

          {/* Leyenda colores ExG */}
          <LeyendaExG/>

          {/* Leyenda clasificación */}
          <div className="px-3 py-2 shrink-0" style={{borderBottom:'1px solid var(--line-dim)'}}>
            <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{color:'var(--ink-faint)'}}>Clasificación de zonas</p>
            <div className="space-y-1">
              {[
                ['#22c55e','Bloques de siembra (del lote)'],
                ['#ff7800','Caminos detectados'],
                ['rgba(60,80,220,0.8)','Tierra / camino (ExG bajo)'],
                ['rgba(200,190,20,0.8)','Vegetación baja'],
                ['rgba(60,200,80,0.8)','Vegetación media'],
                ['rgba(0,255,60,0.8)','Vegetación alta (siembra)'],
              ].map(([color,label])=>(
                <div key={label} className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 rounded-sm shrink-0" style={{background:color,border:'1px solid rgba(255,255,255,0.15)'}}/>
                  <span className="text-[10px]" style={{color:'var(--ink-muted)'}}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Auto-detección */}
          <div className="p-3 space-y-2 shrink-0" style={{borderBottom:'1px solid var(--line-dim)'}}>
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{color:'var(--ink-faint)'}}>
              Auto-Detectar Caminos entre Bloques
            </p>
            <p className="text-[10px]" style={{color:'var(--ink-faint)',opacity:0.7}}>
              Analiza zonas entre bloques. Excluye los bloques de siembra del análisis.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] block mb-0.5" style={{color:'var(--ink-faint)'}}>Ancho min (m)</label>
                <input type="number" step="0.1" min="0.5" value={anchoMin}
                  onChange={e=>setAncho(Number(e.target.value))}
                  className="w-full px-2 py-1 rounded text-xs"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line)',color:'var(--ink)'}}/>
              </div>
              <div>
                <label className="text-[10px] block mb-0.5" style={{color:'var(--ink-faint)'}}>Largo min (m)</label>
                <input type="number" step="0.5" min="2" value={largoMin}
                  onChange={e=>setLargo(Number(e.target.value))}
                  className="w-full px-2 py-1 rounded text-xs"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line)',color:'var(--ink)'}}/>
              </div>
            </div>
            <div>
              <label className="text-[10px] block mb-0.5" style={{color:'var(--ink-faint)'}}>
                Umbral ExG (percentil {umbral}% — más bajo = más caminos)
              </label>
              <input type="range" min="35" max="70" step="1" value={umbral}
                onChange={e=>setUmbral(Number(e.target.value))} className="w-full accent-orange-500"/>
            </div>
            <button onClick={autoDetectar} disabled={autoRun||!hasImage}
              className="w-full py-2 rounded text-xs font-semibold inline-flex items-center justify-center gap-1.5"
              style={{background:'#ff780022',color:'#ff7800',border:'1px solid #ff780055',opacity:autoRun||!hasImage?0.5:1}}>
              {autoRun?<><Loader2 size={12} className="animate-spin"/>Detectando (Worker)…</>:'⚡ Auto-Detectar Caminos'}
            </button>
            {autoMask&&(
              <button onClick={()=>{setAutoMask(null);setAutoUrl('');}} className="w-full py-1 rounded text-[10px]"
                style={{color:'var(--ink-faint)',border:'1px solid var(--line)'}}>
                Limpiar detección
              </button>
            )}
          </div>

          {/* Caminos manuales */}
          {caminos.length>0&&(
            <div className="p-3 space-y-1.5 shrink-0" style={{borderBottom:'1px solid var(--line-dim)'}}>
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{color:'var(--ink-faint)'}}>Caminos manuales</p>
              {caminos.map((c,i)=>(
                <div key={i} className="flex items-center justify-between rounded px-2 py-1.5"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line-dim)'}}>
                  <span className="text-[11px]" style={{color:'#ff7800'}}>C{i+1} · {fmtM2(c.areaM2)}</span>
                  <button onClick={()=>setCam(cs=>cs.filter((_,j)=>j!==i))} className="p-0.5" style={{color:'var(--ink-faint)'}}
                    onMouseEnter={e=>(e.currentTarget.style.color='#f87171')} onMouseLeave={e=>(e.currentTarget.style.color='var(--ink-faint)')}>
                    <Trash2 size={11}/>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Alerta */}
          {alerta&&(
            <div className="mx-3 mt-2 flex gap-1.5 items-start p-2 rounded"
              style={{background:'var(--tone-warn-bg)',border:'1px solid var(--tone-warn-border)'}}>
              <AlertCircle size={12} style={{color:'var(--tone-warn-text)',flexShrink:0,marginTop:1}}/>
              <p className="text-[11px]" style={{color:'var(--tone-warn-ink)'}}>{alerta}</p>
            </div>
          )}

          {/* Tabla de reporte */}
          {totales&&(
            <div className="m-3 rounded-xl overflow-hidden shrink-0" style={{border:'1px solid #123482'}}>
              <div className="px-3 py-2 font-bold text-xs" style={{background:'#123482',color:'#fff'}}>
                Resumen del Lote
              </div>
              {([
                ['Área total lote',   fmtM2(totales.areaTotalM2),                '#adb5bd'],
                ['Área bloques (siembra)', fmtM2(totales.areaSiembraM2),         '#22c55e'],
                ['Zona entre bloques', fmtM2(totales.areaEntreBloquesM2),        '#adb5bd'],
                ['Caminos detectados', fmtM2(totales.areaCaminosM2),             '#ff7800'],
                ['Cobertura siembra', fmtPct(totales.coberturaPct),             '#22c55e'],
                ['Caminos %',         fmtPct(totales.caminosPct),               '#ff7800'],
              ] as [string,string,string][]).map(([lbl,val,color],i)=>(
                <div key={lbl} className="flex justify-between items-center px-3 py-1.5 text-xs"
                  style={{background:i%2===0?'var(--surface-raised)':'var(--surface-overlay)',borderTop:'1px solid var(--line-dim)'}}>
                  <span style={{color:'var(--ink-muted)'}}>{lbl}</span>
                  <span className="font-semibold" style={{color}}>{val}</span>
                </div>
              ))}
            </div>
          )}

          {/* Info bloques — tabla clickeable para resaltar en imagen */}
          {bloquesNorm.length>0&&(
            <div className="shrink-0" style={{borderBottom:'1px solid var(--line-dim)'}}>
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{background:'var(--surface-deep)'}}>
                    <th className="px-3 py-1.5 text-left font-semibold" style={{color:'var(--ink-faint)'}}>Bloque</th>
                    <th className="px-3 py-1.5 text-right font-semibold" style={{color:'var(--ink-faint)'}}>Plantas</th>
                    <th className="px-3 py-1.5 text-right font-semibold" style={{color:'var(--ink-faint)'}}>Área</th>
                  </tr>
                </thead>
                <tbody>
                  {bloquesNorm.map(b=>{
                    const sel=bloqueSelec===b.num;
                    const pl=b.plant_count;
                    const ar=b.area_ha;
                    return (
                      <tr key={b.num}
                        onClick={()=>setBloqueSelec(v=>v===b.num?null:b.num)}
                        className="cursor-pointer transition-colors"
                        style={{
                          background: sel ? '#facc1514' : 'transparent',
                          borderLeft: `2px solid ${sel ? '#facc15' : 'transparent'}`,
                        }}
                        onMouseEnter={e=>(e.currentTarget.style.background=sel?'#facc1520':'#22c55e0a')}
                        onMouseLeave={e=>(e.currentTarget.style.background=sel?'#facc1514':'transparent')}
                      >
                        <td className="px-3 py-1 font-bold" style={{color:sel?'#facc15':'#22c55e'}}>B{b.num}</td>
                        <td className="px-3 py-1 text-right" style={{color:sel?'#facc15':'var(--ink-muted)'}}>
                          {pl!=null ? pl.toLocaleString('es-CR') : '—'}
                        </td>
                        <td className="px-3 py-1 text-right" style={{color:sel?'#facc15':'var(--ink-faint)'}}>
                          {ar!=null ? ar.toFixed(2)+' ha' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Conteo con Motor Python ── */}
          <div className="p-3 space-y-2.5 shrink-0" style={{borderTop:'2px solid var(--emp-accent)',background:'var(--surface-deep)'}}>
            <p className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1.5" style={{color:'var(--emp-accent-txt)'}}>
              <Sprout size={12}/> Conteo de Plantas (Motor)
            </p>

            {/* TIF del motor */}
            <div>
              <button type="button" onClick={()=>tifMotorRef.current?.click()}
                className="w-full py-1.5 rounded text-xs inline-flex items-center justify-center gap-1.5 border"
                style={{borderColor:tifMotorFile?'var(--tone-success-border)':'var(--line)',color:tifMotorFile?'var(--tone-success-text)':'var(--ink-faint)',background:tifMotorFile?'var(--tone-success-bg)':'var(--surface-overlay)'}}>
                <Upload size={11}/>{tifMotorFile?tifMotorFile.name.slice(0,22)+'…':'Cargar ortomosaico TIF'}
              </button>
              <input ref={tifMotorRef} type="file" accept=".tif,.tiff" className="hidden"
                onChange={e=>{const f=e.target.files?.[0];if(f){setTifMotorFile(f);setMotorResult(null);setMotorSaved(false);}e.target.value='';}}/>
            </div>

            {/* Parámetros */}
            <div className="grid grid-cols-3 gap-1.5">
              <div>
                <label className="text-[9px] block mb-0.5" style={{color:'var(--ink-faint)'}}>GSD m/px</label>
                <input type="number" step="0.01" min="0.01" value={gsdMotor}
                  onChange={e=>setGsdMotor(e.target.value)}
                  className="w-full px-1.5 py-1 rounded text-xs"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line)',color:'var(--ink)'}}/>
              </div>
              <div>
                <label className="text-[9px] block mb-0.5" style={{color:'var(--ink-faint)'}}>Camas (auto)</label>
                <input type="number" step="1" min="1" value={camasOverride} placeholder="auto"
                  onChange={e=>setCamasOverride(e.target.value)}
                  className="w-full px-1.5 py-1 rounded text-xs"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line)',color:'var(--ink)'}}/>
              </div>
              <div>
                <label className="text-[9px] block mb-0.5" style={{color:'var(--ink-faint)'}}>Filas/cama</label>
                <input type="number" step="1" min="1" value={filasCama}
                  onChange={e=>setFilasCama(e.target.value)}
                  className="w-full px-1.5 py-1 rounded text-xs"
                  style={{background:'var(--surface-overlay)',border:'1px solid var(--line)',color:'var(--ink)'}}/>
              </div>
            </div>

            {/* Error motor */}
            {motorErr&&(
              <div className="flex gap-1.5 p-2 rounded" style={{background:'var(--tone-warn-bg)',border:'1px solid var(--tone-warn-border)'}}>
                <AlertCircle size={11} style={{color:'var(--tone-warn-text)',flexShrink:0,marginTop:1}}/>
                <p className="text-[10px]" style={{color:'var(--tone-warn-ink)'}}>{motorErr}</p>
              </div>
            )}

            {/* Botón contar */}
            <button onClick={contarPlantas} disabled={motorRunning||!tifMotorFile||bloquesNorm.length===0}
              className="w-full py-2 rounded font-semibold text-xs inline-flex items-center justify-center gap-1.5"
              style={{background:'var(--emp-accent)',color:'#fff',opacity:motorRunning||!tifMotorFile||bloquesNorm.length===0?0.45:1}}>
              {motorRunning?<><Loader2 size={12} className="animate-spin"/>Contando...</>:<><FlaskConical size={12}/>Contar Plantas</>}
            </button>

            {/* Resultados */}
            {motorResult&&motorResult.bloques&&(()=>{
              const totalPl=motorResult.bloques.reduce((s,b)=>s+(b.conteo??0),0);
              const totalHa=motorResult.bloques.reduce((s,b)=>s+(b.area_ha??0),0);
              const densidad=totalHa>0?totalPl/totalHa:0;
              const kpiProm=motorResult.bloques.length?motorResult.bloques.reduce((s,b)=>s+(b.kpi??0),0)/motorResult.bloques.length:0;
              const kpiColor=(v:number)=>v>=90?'#22c55e':v>=70?'#f59e0b':'#f87171';
              const fmtN=(v:number,d=0)=>v.toLocaleString('es-CR',{minimumFractionDigits:d,maximumFractionDigits:d});
              return (
                <div className="space-y-2">
                  {/* KPIs */}
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      {label:'Total plantas',value:fmtN(totalPl),color:'var(--emp-accent-txt)'},
                      {label:'Área',value:`${fmtN(totalHa,2)} ha`,color:'var(--ink)'},
                      {label:'Densidad',value:`${fmtN(densidad,0)} pl/ha`,color:'var(--ink-muted)'},
                      {label:'KPI prom.',value:`${fmtN(kpiProm,1)}%`,color:kpiColor(kpiProm)},
                    ].map(item=>(
                      <div key={item.label} className="rounded p-1.5" style={{background:'var(--surface-overlay)',border:'1px solid var(--line-dim)'}}>
                        <p className="text-[9px] mb-0.5" style={{color:'var(--ink-faint)'}}>{item.label}</p>
                        <p className="text-xs font-bold" style={{color:item.color}}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  {/* Tabla bloques */}
                  <div className="rounded overflow-hidden text-[10px]" style={{border:'1px solid var(--line)'}}>
                    <div className="grid px-2 py-1 font-semibold" style={{gridTemplateColumns:'1fr 2fr 2fr 1.5fr',background:'var(--surface-deep)',color:'var(--ink-faint)'}}>
                      <span>Blq</span><span>Plantas</span><span>Camas</span><span>KPI</span>
                    </div>
                    {motorResult.bloques.map((b,i)=>(
                      <div key={b.bloque_num??i} className="grid px-2 py-1"
                        style={{gridTemplateColumns:'1fr 2fr 2fr 1.5fr',background:i%2===0?'var(--surface-raised)':'var(--surface-overlay)',borderTop:'1px solid var(--line-dim)'}}>
                        <span className="font-bold" style={{color:'var(--emp-accent-txt)'}}>B{b.bloque_num}</span>
                        <span style={{color:'var(--ink)'}}>{fmtN(b.conteo??0)}</span>
                        <span style={{color:'var(--ink-muted)'}}>{b.camas_estimadas??'—'}</span>
                        <span className="font-semibold" style={{color:kpiColor(b.kpi??0)}}>{b.kpi!=null?`${fmtN(b.kpi,1)}%`:'—'}</span>
                      </div>
                    ))}
                  </div>
                  {/* Guardar */}
                  {motorSaved?(
                    <div className="flex items-center gap-1.5 p-2 rounded" style={{background:'var(--tone-success-bg)',border:'1px solid var(--tone-success-border)'}}>
                      <CheckCircle2 size={12} style={{color:'var(--tone-success-text)'}}/>
                      <p className="text-[10px] font-medium" style={{color:'var(--tone-success-text)'}}>Guardado en bloques del lote</p>
                    </div>
                  ):(
                    <button onClick={guardarConteo}
                      className="w-full py-1.5 rounded text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                      style={{background:'var(--tone-success-bg)',color:'var(--tone-success-text)',border:'1px solid var(--tone-success-border)'}}>
                      <Download size={11}/>Guardar en bloques del lote
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Ficha PDF */}
      {fichaOpen && (
        <FichaPDF
          nombreLote={nombreLote || 'Lote'}
          empresaId={empresaId}
          bloques={bloquesNorm}
          perimLote={perimLote}
          onClose={()=>setFichaOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Ficha PDF ────────────────────────────────────────────────────────────────

function computeBBox(pts: Pt[]): {minX:number;minY:number;maxX:number;maxY:number} {
  let minX=1,minY=1,maxX=0,maxY=0;
  for(const [x,y] of pts){ if(x<minX)minX=x; if(x>maxX)maxX=x; if(y<minY)minY=y; if(y>maxY)maxY=y; }
  return {minX,minY,maxX,maxY};
}

function FichaPDF({ nombreLote, empresaId, bloques, perimLote, onClose }: {
  nombreLote: string;
  empresaId?: number;
  bloques: Array<{num:number; pts:Pt[]; plant_count?:number; area_ha?:number}>;
  perimLote: Pt[];
  onClose: ()=>void;
}) {
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [fincaNombre, setFincaNombre] = useState('');

  useEffect(()=>{
    if(!empresaId) return;
    supabase.from('emp_empresas').select('nombre').eq('id', empresaId).single()
      .then(({data})=>{ if(data) setEmpresaNombre((data as any).nombre || ''); });
  },[empresaId]);

  // Calcular bbox global de todos los polígonos
  const allPts = [...perimLote, ...bloques.flatMap(b=>b.pts)];
  const bbox = allPts.length > 0 ? computeBBox(allPts) : {minX:0,minY:0,maxX:1,maxY:1};
  const bw = Math.max(0.001, bbox.maxX - bbox.minX);
  const bh = Math.max(0.001, bbox.maxY - bbox.minY);
  const pad = 0.04;

  // SVG viewBox con padding
  const vx = (bbox.minX - pad*bw) * 1000;
  const vy = (bbox.minY - pad*bh) * 1000;
  const vw = (bw + 2*pad*bw) * 1000;
  const vh = (bh + 2*pad*bh) * 1000;

  function ptsToSvg(pts: Pt[]) {
    return pts.map(([x,y])=>`${(x*1000).toFixed(1)},${(y*1000).toFixed(1)}`).join(' ');
  }

  function centroide(pts: Pt[]): Pt {
    const cx = pts.reduce((s,[x])=>s+x,0)/pts.length;
    const cy = pts.reduce((s,[,y])=>s+y,0)/pts.length;
    return [cx, cy];
  }

  const totalPlantas = bloques.reduce((s,b)=>s+(b.plant_count??0),0);
  const totalHa = bloques.reduce((s,b)=>s+(b.area_ha??0),0);
  const fecha = new Date().toLocaleDateString('es-CR',{day:'2-digit',month:'long',year:'numeric'});

  // Paleta por bloque
  const COLORS = ['#16a34a','#2563eb','#9333ea','#dc2626','#ea580c','#0891b2','#65a30d','#b45309','#7c3aed','#0f766e','#be185d','#1d4ed8'];

  function imprimir() {
    const el = document.getElementById('ficha-pdf-content');
    if(!el) return;
    const w = window.open('','_blank','width=1100,height=820');
    if(!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Ficha — ${nombreLote}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#111;}
        @page{size:A4 landscape;margin:12mm;}
        @media print{body{margin:0;}}
      </style>
      </head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(()=>{ w.print(); }, 400);
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.85)'}}>
      <div className="relative flex flex-col rounded-xl shadow-2xl overflow-hidden" style={{width:'min(1060px,96vw)',maxHeight:'92vh',background:'#1a1a2e',border:'1px solid #334'}}>

        {/* Barra modal */}
        <div className="flex items-center justify-between px-5 py-3 shrink-0" style={{borderBottom:'1px solid #334',background:'#12121e'}}>
          <span className="font-semibold text-sm text-white">Vista previa — Ficha de Inventario</span>
          <div className="flex items-center gap-2">
            <button onClick={imprimir}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-bold"
              style={{background:'#2563eb',color:'#fff'}}>
              <Download size={13}/> Imprimir / Guardar PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:text-white">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Vista previa — A4 landscape 297×210mm → ~1060×750px a 90dpi */}
        <div className="overflow-auto flex-1 p-4 flex justify-center items-start">
          <div id="ficha-pdf-content" style={{
            width:1020, minWidth:1020, background:'#fff', color:'#111',
            fontFamily:"'Segoe UI',Arial,sans-serif", borderRadius:6,
            boxShadow:'0 4px 32px rgba(0,0,0,0.5)', overflow:'hidden',
          }}>

            {/* Header */}
            <div style={{background:'#0f172a',padding:'12px 22px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:36,height:36,borderRadius:8,background:'#22c55e',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{color:'#fff',fontWeight:900,fontSize:16}}>M</span>
                </div>
                <div>
                  <div style={{color:'#94a3b8',fontSize:9,letterSpacing:2,textTransform:'uppercase'}}>Ficha de Inventario de Plantación</div>
                  <div style={{color:'#fff',fontSize:17,fontWeight:700,lineHeight:1.2,whiteSpace:'nowrap'}}>{empresaNombre || '—'}</div>
                </div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{color:'#38bdf8',fontSize:13,fontWeight:600}}>{nombreLote}</div>
                <div style={{color:'#94a3b8',fontSize:10,marginTop:2}}>{fecha}</div>
              </div>
            </div>

            {/* Cuerpo: mapa grande izquierda + tabla compacta derecha */}
            <div style={{display:'flex',gap:0,height:590}}>

              {/* SVG del mapa — 68% del ancho */}
              <div style={{flex:'0 0 690px',padding:'14px 12px 14px 18px',borderRight:'1px solid #e2e8f0',display:'flex',alignItems:'stretch'}}>
                <svg viewBox={`${vx} ${vy} ${vw} ${vh}`} style={{width:'100%',height:'100%',display:'block'}}
                  xmlns="http://www.w3.org/2000/svg">
                  <rect x={vx} y={vy} width={vw} height={vh} fill="#f1f5f9" rx="4"/>
                  {perimLote.length > 2 && (
                    <polygon points={ptsToSvg(perimLote)}
                      fill="#dcfce7" fillOpacity="0.7"
                      stroke="#166534" strokeWidth={vw*0.0025}
                      strokeDasharray={`${vw*0.007} ${vw*0.0035}`}/>
                  )}
                  {bloques.map((b,i)=>{
                    const color = COLORS[i % COLORS.length];
                    const [cx,cy] = centroide(b.pts);
                    return (
                      <g key={b.num}>
                        <polygon points={ptsToSvg(b.pts)}
                          fill={color} fillOpacity="0.22"
                          stroke={color} strokeWidth={vw*0.002}/>
                        <text x={cx*1000} y={cy*1000} textAnchor="middle" dominantBaseline="middle"
                          fontSize={vw*0.025} fontWeight="800" fill={color}
                          style={{textShadow:'0 0 3px #fff'}}>
                          B{b.num}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Tabla compacta — 32% restante */}
              <div style={{flex:1,padding:'12px 16px 12px 14px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
                <div style={{fontSize:9,fontWeight:700,color:'#64748b',letterSpacing:1.5,textTransform:'uppercase',marginBottom:6}}>
                  Resumen de Bloques
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5,flex:1}}>
                  <thead>
                    <tr style={{background:'#0f172a'}}>
                      <th style={{padding:'4px 6px',textAlign:'left',color:'#94a3b8',fontWeight:600}}>Blq</th>
                      <th style={{padding:'4px 6px',textAlign:'right',color:'#94a3b8',fontWeight:600}}>Plantas</th>
                      <th style={{padding:'4px 6px',textAlign:'right',color:'#94a3b8',fontWeight:600}}>Área ha</th>
                      <th style={{padding:'4px 6px',textAlign:'right',color:'#94a3b8',fontWeight:600}}>pl/m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bloques.map((b,i)=>{
                      const color = COLORS[i % COLORS.length];
                      const plm2 = b.plant_count && b.area_ha ? (b.plant_count/(b.area_ha*10000)).toFixed(2) : '—';
                      return (
                        <tr key={b.num} style={{borderBottom:'1px solid #f1f5f9',background: i%2===0?'#fff':'#f8fafc'}}>
                          <td style={{padding:'3px 6px',fontWeight:700,color:color}}>
                            <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                              <span style={{width:7,height:7,borderRadius:1,background:color,display:'inline-block',flexShrink:0}}/>
                              B{b.num}
                            </span>
                          </td>
                          <td style={{padding:'3px 6px',textAlign:'right',fontWeight:600,color:'#1e293b'}}>
                            {b.plant_count!=null ? b.plant_count.toLocaleString('es-CR') : '—'}
                          </td>
                          <td style={{padding:'3px 6px',textAlign:'right',color:'#475569'}}>
                            {b.area_ha!=null ? b.area_ha.toFixed(3) : '—'}
                          </td>
                          <td style={{padding:'3px 6px',textAlign:'right',color:'#475569'}}>{plm2}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#0f172a',borderTop:'2px solid #1e293b'}}>
                      <td style={{padding:'5px 6px',fontWeight:700,color:'#fff',fontSize:10}}>Total</td>
                      <td style={{padding:'5px 6px',textAlign:'right',fontWeight:700,color:'#22c55e',fontSize:11}}>
                        {totalPlantas.toLocaleString('es-CR')}
                      </td>
                      <td style={{padding:'5px 6px',textAlign:'right',fontWeight:600,color:'#e2e8f0',fontSize:10}}>
                        {totalHa.toFixed(3)}
                      </td>
                      <td style={{padding:'5px 6px',textAlign:'right',fontWeight:600,color:'#e2e8f0',fontSize:10}}>
                        {totalHa>0 ? (totalPlantas/(totalHa*10000)).toFixed(2) : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                {/* 4 métricas al pie de la columna */}
                <div style={{marginTop:10,display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                  {[
                    {label:'Bloques',      val: String(bloques.length)},
                    {label:'Área total',   val: totalHa.toFixed(3)+' ha'},
                    {label:'Total plantas',val: totalPlantas.toLocaleString('es-CR')},
                    {label:'Proyección',   val: totalHa>0 ? Math.round(totalPlantas/totalHa).toLocaleString('es-CR')+' pl/ha' : '—'},
                  ].map(({label,val})=>(
                    <div key={label} style={{background:'#f1f5f9',borderRadius:5,padding:'6px 8px',borderLeft:'3px solid #22c55e'}}>
                      <div style={{fontSize:8,color:'#94a3b8',textTransform:'uppercase',letterSpacing:1}}>{label}</div>
                      <div style={{fontSize:13,fontWeight:700,color:'#0f172a',marginTop:1}}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{background:'#0f172a',padding:'7px 22px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{color:'#22c55e',fontSize:11,fontWeight:800,letterSpacing:1.5}}>MYA APLICACIONES</span>
                <span style={{color:'#334155',fontSize:10}}>·</span>
                <span style={{color:'#475569',fontSize:10}}>Sistema de Gestión Agrícola y Trazabilidad</span>
              </div>
              <div style={{color:'#334155',fontSize:10}}>
                Generado el {fecha} · Datos calibrados con tecnología de dron
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Wrapper portal ───────────────────────────────────────────────────────────
export function ConteoPlantasV8Page(props: ConteoPlantasV8Props) {
  if (props.onClose) return ReactDOM.createPortal(<V8Content {...props}/>, document.body);
  return <div className="w-full h-full" style={{position:'relative'}}><V8Content {...props}/></div>;
}

export default ConteoPlantasV8Page;
